import React, { useCallback, useEffect, useState } from "react";
import { Modal, Spin, Tag, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";

import LabelEditor from "../components/LabelEditor";
import * as projectsApi from "../../../../core/data/redux/services/projectsApi";
import { fetchLabels } from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveCompany,
  selectCurrentProject,
  selectLabels,
} from "../../../../core/data/redux/projects/selectors";
import { useProjectCapability } from "../../hooks/useProjectRole";
import { errMsg } from "../../helpers/errMsg.helper";
import { CONFIRM_MODAL_PROPS } from "../../helpers/modal.style";
import { notify } from "../../../../services/toast.config";

import type { Label } from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

const LabelsSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const labels = useSelector(selectLabels);
  const activeCompany = useSelector(selectActiveCompany);
  const canManageLabel = useProjectCapability(project?.id, "MANAGE_LABEL");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<Label | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      await dispatch(fetchLabels(project.id)).unwrap();
    } catch {
      /* silently handled */
    } finally {
      setLoading(false);
    }
  }, [dispatch, project?.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleCreate = useCallback(() => {
    setEditingLabel(null);
    setEditorOpen(true);
  }, []);

  const handleEdit = useCallback((label: Label) => {
    setEditingLabel(label);
    setEditorOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (payload: { name: string; color: string }) => {
      if (!project) return;
      setSubmitting(true);
      try {
        if (editingLabel) {
          await projectsApi.updateLabel(
            activeCompany.company_id,
            activeCompany.company_type,
            editingLabel.id,
            payload
          );
          notify.success("Label updated");
        } else {
          await projectsApi.createLabel(
            activeCompany.company_id,
            activeCompany.company_type,
            { project_id: project.id, ...payload }
          );
          notify.success("Label created");
        }
        await reload();
        setEditorOpen(false);
        setEditingLabel(null);
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to save label"));
        throw e;
      } finally {
        setSubmitting(false);
      }
    },
    [activeCompany, editingLabel, project, reload]
  );

  const handleDelete = useCallback(
    (label: Label) => {
      Modal.confirm({
        title: `Delete label '${label.name}'?`,
        content:
          "Issues currently tagged with this label will keep the link until you remove it from each issue.",
        okText: "Delete",
        okButtonProps: { danger: true },
        ...CONFIRM_MODAL_PROPS,
        onOk: async () => {
          try {
            await projectsApi.deleteLabel(
              activeCompany.company_id,
              activeCompany.company_type,
              label.id
            );
            await reload();
            notify.success("Label deleted");
          } catch (e: any) {
            notify.error(errMsg(e, "Failed to delete label"));
          }
        },
      });
    },
    [activeCompany, reload]
  );

  if (!project) {
    return (
      <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
        <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }} />
            <p className="mt-3 text-muted mb-0">Loading project…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
      <div className="card-header border-0 pb-0 d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-1 d-flex align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
            <i className="ti ti-tag" style={{ color: "#3b82f6", fontSize: 20, flexShrink: 0 }} />
            <span>Labels</span>
          </h5>
          <span className="text-muted small">
            {labels.length} label{labels.length === 1 ? "" : "s"} in this project
          </span>
        </div>
        {canManageLabel && (
          <button type="button" className="btn btn-info btn-sm d-inline-flex align-items-center gap-1" style={{ borderRadius: 8, whiteSpace: "nowrap" }} onClick={handleCreate}>
            <i className="ti ti-plus" style={{ fontSize: 16 }} />
            <span>New Label</span>
          </button>
        )}
      </div>
      <div className="card-body bg-white rounded-3">
        {loading && labels.length === 0 ? (
          <div className="text-center py-3">
            <Spin />
          </div>
        ) : labels.length === 0 ? (
          <div className="text-center py-4">
            <i className="ti ti-tag" style={{ fontSize: 40, color: "#cbd5e1" }} />
            <p className="text-muted mt-2 mb-0">No labels yet. Create labels to categorize issues.</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {labels.map((label) => (
              <div
                key={label.id}
                className="d-flex align-items-center gap-3 px-3 py-2"
                style={{ borderRadius: 10, border: "1px solid #e2e8f0", transition: "all 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#93c5fd"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
              >
                <div className="d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, borderRadius: 8, background: label.color ? label.color + "18" : "#f1f5f9", flexShrink: 0 }}>
                  <span className="d-inline-block rounded-circle" style={{ width: 14, height: 14, background: label.color || "#94a3b8" }} />
                </div>
                <div className="flex-grow-1 min-width-0">
                  <span className="fw-semibold" style={{ fontSize: 13.5 }}>{label.name}</span>
                  <div className="text-muted" style={{ fontSize: 11.5 }}>{label.color || "No color"}</div>
                </div>
                {canManageLabel && (
                  <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                    <Tooltip title="Edit"><button type="button" className="btn btn-sm btn-light d-flex align-items-center justify-content-center" style={{ borderRadius: 8, width: 32, height: 32 }} onClick={() => handleEdit(label)}><i className="ti ti-edit" style={{ color: "#f59e0b", fontSize: 15 }} /></button></Tooltip>
                    <Tooltip title="Delete"><button type="button" className="btn btn-sm btn-light d-flex align-items-center justify-content-center" style={{ borderRadius: 8, width: 32, height: 32 }} onClick={() => handleDelete(label)}><i className="ti ti-trash" style={{ color: "#ef4444", fontSize: 15 }} /></button></Tooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <LabelEditor
        open={editorOpen}
        initialLabel={editingLabel}
        submitting={submitting}
        onSubmit={handleSubmit}
        onCancel={() => {
          setEditorOpen(false);
          setEditingLabel(null);
        }}
      />
    </div>
  );
};

export default LabelsSettings;
